import React, {useState} from 'react';
import {Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import NationalDashboardShell from '../components/NationalDashboard.comp';
import PlanGate from '../components/PlanGate.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, rowsFrom, rowMatches} from '../../DashboardCommon/dashboardCards';
import {RowCell} from '../../DashboardCommon/SuppressedValue.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';
import {NAVY, ORANGE, TEAL, PageHeader, BandChip, headCell, bodyCell} from '../components/widgets.comp';

/* Slides 44-45 — Learning Impact Tracker (By Program) and by COS Area (National).
   Live data (ND.S_13), in this page's own layout:
     By Program   C_2 — the card lists Country first, but a National dashboard is one country, so
                  that column is not drawn; Region · School · Program · Target Area · Start Date ·
                  Enrolled · Completed · Completion Rate · Pre · Post · Impact Rate · Signal.
     By COS Area  C_3 — COS Area · Baseline · Schools Covered · Students Reached · Pre · Post ·
                  Impact Rate · Reach · Signal (no "Countries Covered" for National).
   Every column sorts and filters from its header (QA 9 Oct 2026 — the old drop-down filter bar
   is gone); the search box above the By Program table matches any shown cell. */
const emptyRow = (cols, text) => (
    <TableRow>
        <TableCell colSpan={cols} sx={{...bodyCell, textAlign: 'center', py: 4, color: '#6A7282'}}>{text}</TableCell>
    </TableRow>
);

const signalColor = (signal) => {
    if (signal === 'Strong') return TEAL;
    if (signal === 'Moderate') return '#EFA020';
    return '#6A7282';
};
const signalChip = (signal) => (!signal || signal === '—' ? '—' : <BandChip label={signal} color={signalColor(signal)}/>);

const SIGNAL_ORDER = ['Strong', 'Moderate', 'Early', 'Weak'];
const PROGRAM_COLUMNS = [
    {key: 'region', label: 'Region', type: 'text', align: 'left'},
    {key: 'school', label: 'School', type: 'text', align: 'left'},
    {key: 'program', label: 'Program', type: 'text', align: 'left'},
    {key: 'targetArea', label: 'Target Area', type: 'text', align: 'left'},
    {key: 'start', label: 'Start Date', type: 'date'},
    {key: 'enrolled', label: 'Students Enrolled', type: 'number'},
    {key: 'completed', label: 'Students Completed', type: 'number'},
    {key: 'rate', label: 'Completion Rate', type: 'percent'},
    {key: 'pre', label: 'Avg. Pre-Assessment', type: 'number'},
    {key: 'post', label: 'Avg. Post-Assessment', type: 'number'},
    {key: 'impact', label: 'Avg. Impact Rate', type: 'percent'},
    {key: 'signal', label: 'Impact Signal', type: 'band', order: SIGNAL_ORDER},
];
const AREA_COLUMNS = [
    {key: 'area', label: 'COS Area', type: 'text', align: 'left'},
    {key: 'baseline', label: 'Baseline Score', type: 'number'},
    {key: 'schools', label: 'Schools Covered', type: 'number'},
    {key: 'students', label: 'Students Reached', type: 'number'},
    {key: 'pre', label: 'Avg. Pre-Assessment', type: 'number'},
    {key: 'post', label: 'Avg. Post-Assessment', type: 'number'},
    {key: 'impact', label: 'Avg. Impact Rate', type: 'percent'},
    {key: 'reach', label: 'Reach', type: 'percent'},
    {key: 'signal', label: 'Impact Signal', type: 'band', order: SIGNAL_ORDER},
];
const frame = {
    border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'auto',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
};
const headerCells = (t, columns) => columns.map((c, i) => (
    <SortFilterHeaderCell key={c.key} {...t.headerProps(c.key)} sx={{...headCell, textAlign: c.align || 'center', ...(i === 0 ? {pl: 2.5} : {})}}/>
));
const zebra = (i) => ({backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'});

// The tables sit in their own components so the sort/filter hook runs below the page's loading branches.
const ByProgramTable = ({rows, query, onQuery}) => {
    const t = useTableControls(rows, PROGRAM_COLUMNS);
    return (
        <>
            <TableToolbar {...t} query={query} onQuery={onQuery} placeholder="Search by region, school, program…"/>
            <TableContainer sx={frame}>
                <Table sx={{minWidth: 1200}}>
                    <TableHead>
                        <TableRow>{headerCells(t, PROGRAM_COLUMNS)}</TableRow>
                    </TableHead>
                    <TableBody>
                        {t.rows.length === 0 && emptyRow(12, rows.length ? 'No row matches the search and filters.' : 'No learning impact recorded yet.')}
                        {t.rows.map((row, i) => (
                            <TableRow key={`${row.school}-${row.program}-${rows.indexOf(row)}`} sx={zebra(i)}>
                                <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.region}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.school}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.program}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.targetArea}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.start}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.enrolled}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.completed}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.rate}</TableCell>
                                {/* Score columns of a row under the n floor arrive flagged: placeholder, no chip. */}
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}><RowCell row={row} k="pre"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}><RowCell row={row} k="post"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center', fontWeight: 700}}><RowCell row={row} k="impact"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row._suppressed?.signal ? <RowCell row={row} k="signal"/> : signalChip(row.signal)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const ByCosAreaTable = ({rows}) => {
    const t = useTableControls(rows, AREA_COLUMNS);
    return (
        <>
            <TableToolbar {...t}/>
            <TableContainer sx={frame}>
                <Table sx={{minWidth: 1000}}>
                    <TableHead>
                        <TableRow>{headerCells(t, AREA_COLUMNS)}</TableRow>
                    </TableHead>
                    <TableBody>
                        {t.rows.length === 0 && emptyRow(9, rows.length ? 'No row matches the filters.' : 'No learning impact recorded yet.')}
                        {t.rows.map((row, i) => (
                            <TableRow key={`${row.area}-${rows.indexOf(row)}`} sx={zebra(i)}>
                                <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.area}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}><RowCell row={row} k="baseline"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.schools}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.students}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}><RowCell row={row} k="pre"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}><RowCell row={row} k="post"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center', fontWeight: 700}}><RowCell row={row} k="impact"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.reach}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row._suppressed?.signal ? <RowCell row={row} k="signal"/> : signalChip(row.signal)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const LEARNING_IMPACT_SECTION_CODE = 'S_13';
const CARD_SECTIONS = [LEARNING_IMPACT_SECTION_CODE];

const LearningImpactPage = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [query, setQuery] = useState('');
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });

    const programCard = findCard(dashboardData, LEARNING_IMPACT_SECTION_CODE, 'C_2');
    const programRows = rowsFrom(programCard, {
        region: 'Region', school: 'School', program: 'Program', targetArea: 'Target Area',
        start: 'Start Date', enrolled: 'Students Enrolled', completed: 'Students Completed', rate: ['Completion Rate %', 'Completion Rate'],
        pre: 'Avg. Pre', post: 'Avg. Post', impact: ['Avg. Impact Rate %', 'Avg. Impact Rate'], signal: 'Impact Signal',
    });
    const areaRows = rowsFrom(findCard(dashboardData, LEARNING_IMPACT_SECTION_CODE, 'C_3'), {
        area: 'COS Area', baseline: 'Baseline Score', schools: 'Schools Covered', students: 'Students Reached',
        pre: 'Avg. Pre', post: 'Avg. Post', impact: ['Avg. Impact Rate %', 'Avg. Impact Rate'], reach: ['Reach %', 'Reach'], signal: 'Impact Signal',
    });
    const q = query.trim().toLowerCase();
    const shownPrograms = programRows.filter((r) => rowMatches(r, q));

    const tabs = (
        <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            TabIndicatorProps={{style: {backgroundColor: ORANGE}}}
            sx={{
                borderBottom: '1px solid #E5E7EB',
                mb: 2,
                '& .MuiTab-root': {textTransform: 'none', fontWeight: 600, fontSize: '14px', color: '#6A7282', px: 2},
                '& .Mui-selected': {color: `${ORANGE} !important`},
            }}
        >
            <Tab label="By Program"/>
            <Tab label="By COS Area"/>
        </Tabs>
    );

    if (loading) {
        return (
            <NationalDashboardShell tabs={tabs}>
                <CircularLoader />
            </NationalDashboardShell>
        );
    }

    if (!dashboardData) {
        return (
            <NationalDashboardShell tabs={tabs}>
                <NoDataAvailable />
            </NationalDashboardShell>
        );
    }

    return (
        <NationalDashboardShell tabs={tabs}>
            <PageHeader
                title={activeTab === 0 ? 'Learning Impact Tracker' : 'Learning Impact Tracker by COS Area'}
                subtitle="Review learning improvement signals from deployed programs."
            />

            {activeTab === 0 ? (
                <ByProgramTable rows={shownPrograms} query={query} onQuery={setQuery}/>
            ) : <ByCosAreaTable rows={areaRows}/>}
        </NationalDashboardShell>
    );
};

// P1 + LC (dashboard catalog). Gated outside the page so a redirected user never fetches S_13.
const GatedLearningImpactPage = () => <PlanGate packages={['P1', 'LC']}><LearningImpactPage/></PlanGate>;

export default GatedLearningImpactPage;
